import {
  Badge,
  Button,
  ChannelBadge,
  ChannelFilter,
  buttonVariants,
  Card,
  EmptyState,
  ErrorState,
  formatYearMonth,
  MonthGrid,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Separator,
  SkeletonTable,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  useToast,
} from "@forge/ui";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileArchive,
  FileSpreadsheet,
  ShoppingBag,
} from "lucide-react";
import { useState } from "react";
import { ListSearch } from "../../components/list-search";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { useChannels } from "../../lib/channel";
import { listCardClassName } from "../../lib/list-card";
import { downloadAuthenticated } from "../../lib/download";
import { formatBRL, formatDateTime } from "../../lib/format";
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_VARIANTS,
} from "../../lib/order-status";

const PAGE_SIZE = 25;

type OrdersSearch = {
  page: number;
  status?: string;
  accountId?: string;
  provider?: "mercadolivre" | "shopee";
  q?: string;
  month?: string;
};

export const Route = createFileRoute("/_app/orders")({
  validateSearch: (search: Record<string, unknown>): OrdersSearch => ({
    page: Number(search.page ?? 1) || 1,
    status: typeof search.status === "string" ? search.status : undefined,
    accountId:
      typeof search.accountId === "string" ? search.accountId : undefined,
    provider:
      search.provider === "mercadolivre" || search.provider === "shopee"
        ? search.provider
        : undefined,
    q: typeof search.q === "string" && search.q.trim() ? search.q : undefined,
    month:
      typeof search.month === "string" && /^\d{4}-\d{2}$/.test(search.month)
        ? search.month
        : undefined,
  }),
  staticData: { layout: "wide" },
  component: OrdersPage,
});

function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function OrdersPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const toast = useToast();
  const [month, setMonth] = useState(currentYearMonth);

  const channels = useChannels();
  const accounts = useQuery({
    queryKey: ["ml-accounts"],
    queryFn: async () => unwrap(await api.ml.accounts.get()),
  });

  const orders = useQuery({
    queryKey: ["orders", search],
    queryFn: async () =>
      unwrap(
        await api.orders.get({
          query: {
            offset: (search.page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            status: search.status as never,
            accountId: search.accountId,
            provider: search.provider,
            q: search.q,
            month: search.month,
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const total = orders.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const csvQuery = () => {
    const params = new URLSearchParams({ month });
    if (search.status) params.set("status", search.status);
    if (search.accountId) params.set("accountId", search.accountId);
    return params.toString();
  };

  const exportCsv = useMutation({
    mutationFn: () =>
      downloadAuthenticated(`/orders/export.csv?${csvQuery()}`, `pedidos-${month}.csv`),
    onError: (error) =>
      toast.add({
        type: "error",
        title: "Não foi possível exportar o CSV",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      }),
  });

  const exportXml = useMutation({
    mutationFn: () =>
      downloadAuthenticated(
        `/orders/invoices.zip?month=${month}`,
        `nfe-${month}.zip`,
      ),
    onError: (error) =>
      toast.add({
        type: "error",
        title: "Não foi possível baixar as NF-e",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      }),
  });

  return (
    <>
      <PageHeader
        title="Pedidos"
        description={`${total} pedido${total === 1 ? "" : "s"}${search.q || search.month ? " neste filtro" : " em todas as contas"}`}
        actions={
          <>
            <ListSearch
              value={search.q ?? ""}
              placeholder="Código, comprador ou SKU"
              onValueChange={(value) =>
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    q: value.trim() || undefined,
                    page: 1,
                  }),
                })
              }
            />
            <Popover>
              <PopoverTrigger
                render={
                  <button
                    type="button"
                    className={buttonVariants({ variant: "secondary" })}
                  >
                    {search.month ? formatYearMonth(search.month) : "Todos os meses"}
                  </button>
                }
              />
              <PopoverContent align="end" className="w-60">
                <button
                  type="button"
                  onClick={() =>
                    void navigate({
                      search: (prev) => ({
                        ...prev,
                        month: undefined,
                        page: 1,
                      }),
                    })
                  }
                  className="mb-2 w-full rounded-control px-2 py-1.5 text-left text-2xs font-medium text-ink-secondary transition-transform duration-100 ease-swift active:scale-[0.97] [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken"
                >
                  Todos os meses
                </button>
                <MonthGrid
                  value={search.month ?? month}
                  onValueChange={(value) => {
                    setMonth(value);
                    void navigate({
                      search: (prev) => ({
                        ...prev,
                        month: value,
                        page: 1,
                      }),
                    });
                  }}
                  max={currentYearMonth()}
                  className="w-full"
                />
              </PopoverContent>
            </Popover>
            <Select
              value={search.status ?? null}
              itemToStringLabel={(value) =>
                value == null
                  ? "Todos os status"
                  : (ORDER_STATUS_LABELS[value] ?? String(value))
              }
              onValueChange={(value) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    status: (value as string) ?? undefined,
                    page: 1,
                  }),
                })
              }
            >
              <SelectTrigger placeholder="Todos os status" />
              <SelectContent>
                <SelectItem value={null}>Todos os status</SelectItem>
                {Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={search.accountId ?? null}
              itemToStringLabel={(value) =>
                value == null
                  ? "Todas as contas"
                  : (accounts.data?.accounts.find((account) => account.id === value)
                      ?.displayName ?? String(value))
              }
              onValueChange={(value) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    accountId: (value as string) ?? undefined,
                    page: 1,
                  }),
                })
              }
            >
              <SelectTrigger placeholder="Todas as contas" />
              <SelectContent>
                <SelectItem value={null}>Todas as contas</SelectItem>
                {accounts.data?.accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger
                render={
                  <button
                    type="button"
                    className={buttonVariants({ variant: "secondary" })}
                  >
                    <Download />
                    Exportar
                  </button>
                }
              />
              <PopoverContent align="end" className="w-60">
                <MonthGrid
                  value={month}
                  onValueChange={setMonth}
                  max={currentYearMonth()}
                  className="w-full"
                />
                <Separator className="my-3" />
                <p className="mb-2 text-2xs text-ink-muted">
                  {formatYearMonth(month)}
                </p>
                <div className="flex flex-col gap-1.5">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="justify-start"
                    loading={exportCsv.isPending}
                    onClick={() => exportCsv.mutate()}
                  >
                    <FileSpreadsheet />
                    CSV gerencial de pedidos
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="justify-start"
                    loading={exportXml.isPending}
                    onClick={() => exportXml.mutate()}
                  >
                    <FileArchive />
                    XMLs das notas (.zip)
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </>
        }
      />

      <ChannelFilter
        className="mb-3"
        value={search.provider}
        channels={channels.providers.map((provider) => ({ provider }))}
        onChange={(provider) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              provider:
                provider === "mercadolivre" || provider === "shopee"
                  ? provider
                  : undefined,
              page: 1,
            }),
          })
        }
      />

      <Card>
        {orders.isLoading ? (
          <SkeletonTable rows={10} columns={7} />
        ) : orders.isError ? (
          <ErrorState
            title="Não foi possível carregar os pedidos"
            error={orders.error}
            retrying={orders.isFetching}
            onRetry={() => void orders.refetch()}
          />
        ) : orders.data && orders.data.orders.length > 0 ? (
          <>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Pedido</TableHead>
                    <TableHead>Itens</TableHead>
                    <TableHead>Comprador</TableHead>
                    <TableHead>Conta</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.data.orders.map((order) => (
                    <TableRow key={order.id} className="cursor-pointer">
                      <TableCell className="font-mono text-2xs text-ink">
                        <Link
                          to="/orders/$id"
                          params={{ id: order.id }}
                          className="hover:text-ember"
                        >
                          #{order.externalOrderId}
                        </Link>
                      </TableCell>
                      <TableCell className="max-w-56">
                        {order.items.length === 1 ? (
                          <Tooltip content={order.items[0]!.title}>
                            <span className="block truncate">
                              {order.items[0]!.quantity}× {order.items[0]!.title}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="text-ink-muted">
                            {order.items.length} itens
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-ink-muted">
                        {order.buyerNickname ?? order.buyerName ?? "—"}
                      </TableCell>
                      <TableCell>
                        <ChannelBadge
                          provider={order.accountProvider}
                          nickname={order.accountNickname}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={ORDER_STATUS_VARIANTS[order.status] ?? "neutral"}
                          dot
                        >
                          {ORDER_STATUS_LABELS[order.status] ?? order.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-ink">
                        {formatBRL(order.totalAmount)}
                      </TableCell>
                      <TableCell className="text-right text-2xs text-ink-muted">
                        {formatDateTime(order.placedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <ul className="divide-y divide-line md:hidden">
              {orders.data.orders.map((order) => (
                <li key={order.id}>
                  <Link
                    to="/orders/$id"
                    params={{ id: order.id }}
                    className={listCardClassName}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-mono text-[0.8125rem] font-medium text-ink">
                        #{order.externalOrderId}
                      </p>
                      <Badge
                        variant={ORDER_STATUS_VARIANTS[order.status] ?? "neutral"}
                        dot
                      >
                        {ORDER_STATUS_LABELS[order.status] ?? order.status}
                      </Badge>
                    </div>
                    <p className="mt-1 truncate text-2xs text-ink-secondary">
                      {order.items.length === 1
                        ? `${order.items[0]!.quantity}× ${order.items[0]!.title}`
                        : `${order.items.length} itens`}
                    </p>
                    <p className="mt-1 text-2xs text-ink-faint">
                      {order.buyerNickname ?? order.buyerName ?? "—"}
                      {" · "}
                      {order.accountNickname}
                      {" · "}
                      {formatBRL(order.totalAmount)}
                      {" · "}
                      {formatDateTime(order.placedAt)}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="flex items-center justify-between border-t border-line px-3 py-2">
              <p className="text-2xs text-ink-muted">
                Página {search.page} de {pageCount}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={search.page <= 1}
                  onClick={() =>
                    navigate({
                      search: (prev) => ({ ...prev, page: search.page - 1 }),
                    })
                  }
                >
                  <ChevronLeft />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={search.page >= pageCount}
                  onClick={() =>
                    navigate({
                      search: (prev) => ({ ...prev, page: search.page + 1 }),
                    })
                  }
                >
                  <ChevronRight />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <EmptyState
            icon={<ShoppingBag />}
            title="Nenhum pedido encontrado"
            description="Ajuste os filtros ou importe pedidos na página de Conexões."
          />
        )}
      </Card>
    </>
  );
}
